'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import PageHeader from '@/components/shared/PageHeader';
import SearchBar from '@/components/shared/SearchBar';
import FilterRow from '@/components/shared/FilterRow';
import Badge from '@/components/shared/Badge';
import EmptyState from '@/components/shared/EmptyState';

import { ThreadCardSkeleton } from '@/components/shared/Skeletons';
import { mockThreads, ConversationThread } from '@/data/mockConversations';
import { MessageSquare, Pin, CheckCircle, HelpCircle, Plus, Search, Lock, ArrowRight } from 'lucide-react';

const categoryFilters = [
  { label: 'All', value: 'all' },
  { label: 'General', value: 'General' },
  { label: 'Recommendations', value: 'Recommendations' },
  { label: 'Marketplace', value: 'Marketplace' },
  { label: 'Fitness & Wellness', value: 'Fitness & Wellness' },
  { label: 'Pets', value: 'Pets' },
  { label: 'Parenting & Family', value: 'Parenting & Family' },
  { label: 'Events & Social', value: 'Events & Social' },
  { label: 'Lost & Found', value: 'Lost & Found' },
  { label: 'Owners Only', value: 'Owners Only' },
];

function getPostTypeBadge(type: ConversationThread['postType']) {
  const map: Record<ConversationThread['postType'], 'question' | 'recommendation' | 'discussion' | 'offer' | 'alert' | 'poll'> = {
    'Question': 'question',
    'Recommendation Request': 'recommendation',
    'Discussion': 'discussion',
    'Offer': 'offer',
    'Alert': 'alert',
    'Poll': 'poll',
  };
  return map[type];
}

function AvatarInitials({ name }: { name: string }) {
  const initials = name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div
      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-display font-bold text-[11px]"
      style={{
        background: 'linear-gradient(135deg, rgba(76,201,255,0.20), rgba(122,92,255,0.20))',
        border: '1px solid rgba(76,201,255,0.20)',
        color: '#4CC9FF',
      }}
    >
      {initials}
    </div>
  );
}

function ThreadCard({ thread }: { thread: ConversationThread }) {
  if (thread.isOwnersOnly) {
    return (
      <div className="neon-card p-5">
        <div className="flex items-start gap-3">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(76,201,255,0.08)', border: '1px solid rgba(76,201,255,0.15)' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4CC9FF" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <Badge variant="ownersOnly" />
            </div>
            <p className="text-[14px] font-medium" style={{ color: 'rgba(242,244,255,0.50)' }}>
              {thread.title}
            </p>
            <p className="text-[12px] mt-1" style={{ color: 'rgba(167,163,196,0.40)' }}>
              Visible to verified owners only
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Link href={`/conversations/thread/${thread.slug}`} className="block group focus-visible:outline-none">
      <div className="neon-card p-5 cursor-pointer group-focus-visible:border-[rgba(76,201,255,0.35)] group-focus-visible:shadow-[0_0_0_3px_rgba(76,201,255,0.15)]">
        <div className="flex items-start gap-3">
          <AvatarInitials name={thread.authorName} />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 mb-2">
              <Badge variant={getPostTypeBadge(thread.postType)} label={thread.postType} />
              <span className="text-[11px]" style={{ color: 'rgba(167,163,196,0.50)' }}>{thread.category}</span>
              {thread.isPinned && <Badge variant="pinned" label="Pinned" />}
              {thread.isSolved && <Badge variant="solved" label="Solved" />}
            </div>
            <h3 className="font-display font-semibold text-[14.5px] leading-snug mb-1.5" style={{ color: '#F2F4FF' }}>
              {thread.title}
            </h3>
            <p className="text-[13px] line-clamp-2 leading-relaxed mb-3" style={{ color: 'rgba(167,163,196,0.75)' }}>
              {thread.body}
            </p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-[12px]" style={{ color: 'rgba(167,163,196,0.55)' }}>
                  {thread.authorName} · {thread.authorTower}
                </span>
              </div>
              <div className="flex items-center gap-3 text-[12px]" style={{ color: 'rgba(167,163,196,0.50)' }}>
                <span>{thread.replyCount} replies</span>
                <span>{thread.helpfulCount} helpful</span>
                <span>{thread.lastActivity}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

// Looking For strip
function LookingForStrip() {
  const items = [
    'Dog walker · Tower 2',
    'Plumber recommendation',
    'Yoga partner · mornings',
    'Furniture pickup help',
    'Parking spot swap · T1',
  ];
  return (
    <div style={{ background: '#0C0A16', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-3">
        <div className="flex items-center gap-4 overflow-x-auto scrollbar-hide">
          <span className="text-[11px] font-semibold tracking-wider uppercase flex-shrink-0" style={{ color: 'rgba(167,163,196,0.45)' }}>
            Looking for
          </span>
          <div className="flex items-center gap-2 flex-shrink-0">
            {items.map((item) => (
              <span
                key={item}
                className="text-[12px] px-3 py-1 rounded-full flex-shrink-0 cursor-pointer transition-all duration-150 hover:border-[rgba(76,201,255,0.25)] hover:text-[rgba(242,244,255,0.90)] hover:bg-white/[0.07] active:scale-95"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#A7A3C4' }}
              >
                {item}
              </span>
            ))}
          </div>
          <Link href="/conversations/thread/dog-walking-services" className="text-[12px] flex-shrink-0 transition-all duration-150 hover:text-[rgba(76,201,255,0.80)] hover:underline underline-offset-2 active:opacity-70 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[rgba(76,201,255,0.40)] rounded" style={{ color: '#4CC9FF' }}>
            + Post a request
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function ConversationsPage() {
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [isLoading] = useState(false);

  const pinned = mockThreads.filter((t) => t.isPinned);
  const solved = mockThreads.filter((t) => t.isSolved && !t.isPinned);
  const unanswered = mockThreads.filter((t) => t.replyCount === 0 && !t.isPinned);

  const filtered = mockThreads.filter((t) => {
    const matchesSearch =
      !search ||
      t.title.toLowerCase().includes(search.toLowerCase()) ||
      t.body.toLowerCase().includes(search.toLowerCase()) ||
      t.authorName.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = activeFilter === 'all' || t.category === activeFilter;
    return matchesSearch && matchesFilter;
  });

  // Mock: not approved — show teaser state
  const isApproved = false;

  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="dark" />
      <main className="pt-16">
        <PageHeader
          eyebrow="Conversations"
          title="Community Discussions"
          description="A private forum for Panorama residents. Ask questions, share recommendations, and stay connected to building life."
          action={
            <button className="btn-primary text-[13px] px-4 py-2.5 flex items-center gap-2">
              <Plus size={15} /> Start a Conversation
            </button>
          }
        />

        {/* Private launch teaser banner */}
        {!isApproved && (
          <div
            className="border-b"
            style={{ background: 'rgba(122,92,255,0.04)', borderColor: 'rgba(122,92,255,0.15)' }}
          >
            <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Lock size={14} style={{ color: '#9B7FFF', flexShrink: 0 }} />
                <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(167,163,196,0.80)' }}>
                  <span style={{ color: '#F2F4FF' }}>Private Conversations</span> is part of the Panorama resident network. Access is rolling out in waves for verified residents.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Link href="/request-access" className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg transition-all duration-150" style={{ background: 'rgba(122,92,255,0.15)', border: '1px solid rgba(122,92,255,0.30)', color: '#C4BEFF' }}>
                  Request Access <ArrowRight size={11} />
                </Link>
                <Link href="/login" className="text-[12px] font-medium px-3 py-1.5 rounded-lg transition-all duration-150" style={{ color: 'rgba(167,163,196,0.60)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  Sign In
                </Link>
              </div>
            </div>
          </div>
        )}

        <LookingForStrip />

        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-8">
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1 max-w-sm">
              <SearchBar placeholder="Search conversations..." value={search} onChange={setSearch} />
            </div>
          </div>
          <div className="mb-8">
            <FilterRow filters={categoryFilters} active={activeFilter} onChange={setActiveFilter} />
          </div>

          {isLoading && (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => <ThreadCardSkeleton key={i} />)}
            </div>
          )}

          {!isLoading && filtered.length === 0 && (
            <EmptyState
              title="No conversations found"
              description="Try adjusting your search or category filter, or start a new conversation."
              icon={<MessageSquare size={22} style={{ color: '#4CC9FF' }} />}
              action={
                <button onClick={() => { setSearch(''); setActiveFilter('all'); }} className="btn-secondary text-[13px] px-4 py-2">
                  Clear filters
                </button>
              }
            />
          )}

          {!isLoading && filtered.length > 0 && (
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
              <div className="xl:col-span-2 space-y-8">
                {/* Pinned */}
                {pinned.length > 0 && activeFilter === 'all' && !search && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Pin size={13} style={{ color: '#F0A832' }} />
                      <h2 className="text-[12px] font-semibold uppercase tracking-wider" style={{ color: '#F0A832' }}>Pinned</h2>
                    </div>
                    <div className="space-y-3">
                      {pinned.map((t) => <ThreadCard key={t.id} thread={t} />)}
                    </div>
                  </div>
                )}

                {/* Recent */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <MessageSquare size={13} style={{ color: '#A7A3C4' }} />
                    <h2 className="text-[12px] font-semibold uppercase tracking-wider" style={{ color: 'rgba(167,163,196,0.55)' }}>
                      {search || activeFilter !== 'all' ? 'Results' : 'Recent Discussions'}
                    </h2>
                  </div>
                  <div className="space-y-3">
                    {filtered.filter((t) => !t.isPinned).map((t) => <ThreadCard key={t.id} thread={t} />)}
                  </div>
                </div>

                {/* Solved */}
                {solved.length > 0 && activeFilter === 'all' && !search && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle size={13} style={{ color: '#2DD4A0' }} />
                      <h2 className="text-[12px] font-semibold uppercase tracking-wider" style={{ color: '#2DD4A0' }}>Solved</h2>
                    </div>
                    <div className="space-y-3">
                      {solved.map((t) => <ThreadCard key={t.id} thread={t} />)}
                    </div>
                  </div>
                )}

                {/* Cross-links */}
                <div
                  className="pt-8 border-t"
                  style={{ borderColor: 'rgba(255,255,255,0.06)' }}
                >
                  <p className="text-[11px] font-semibold tracking-[0.10em] uppercase mb-4" style={{ color: 'rgba(167,163,196,0.35)' }}>
                    Also in the community
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Link href="/directory" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      Resident Directory
                    </Link>
                    <Link href="/services" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      Resident Services
                    </Link>
                    <Link href="/marketplace" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      Marketplace
                    </Link>
                    <Link href="/events" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      Events
                    </Link>
                    <Link href="/resources" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      Resources
                    </Link>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="hidden xl:block space-y-4">
                <div className="neon-card p-5">
                  <h3 className="font-display font-semibold text-[14px] mb-4" style={{ color: '#F2F4FF' }}>Community Activity</h3>
                  <div className="space-y-3">
                    {[
                      { label: 'Active threads this week', value: '38', color: '#4CC9FF' },
                      { label: 'Helpful marks given', value: '214', color: '#2DD4A0' },
                      { label: 'Questions resolved', value: '12', color: '#9B7FFF' },
                      { label: 'New residents joined', value: '4', color: '#4CC9FF' },
                    ].map((stat) => (
                      <div key={stat.label} className="flex items-center justify-between">
                        <span className="text-[13px]" style={{ color: '#A7A3C4' }}>{stat.label}</span>
                        <span className="font-bold text-[15px] tabular-nums" style={{ color: stat.color }}>{stat.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div
                  className="neon-card p-5"
                  style={{ background: 'rgba(76,201,255,0.04)', border: '1px solid rgba(76,201,255,0.12)' }}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="ownersOnly" />
                  </div>
                  <p className="text-[13px] leading-relaxed mb-3" style={{ color: 'rgba(167,163,196,0.70)' }}>
                    Some threads are visible only to verified unit owners. They appear in your feed once ownership is confirmed.
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#4CC9FF' }} />
                    <span className="text-[12px]" style={{ color: 'rgba(167,163,196,0.45)' }}>3 owners-only threads this week</span>
                  </div>
                </div>

                {unanswered.length > 0 && (
                  <div className="neon-card p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <HelpCircle size={13} style={{ color: '#A7A3C4' }} />
                      <h3 className="text-[12px] font-semibold uppercase tracking-wider" style={{ color: 'rgba(167,163,196,0.55)' }}>Unanswered</h3>
                    </div>
                    <div className="space-y-2">
                      {unanswered.slice(0, 3).map((t) => (
                        <Link key={t.id} href={`/conversations/thread/${t.slug}`} className="block text-[13px] transition-colors duration-150 hover:text-[#4CC9FF]" style={{ color: 'rgba(242,244,255,0.65)' }}>
                          {t.title}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
